import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  HedgePlanTermReportModel,
  HedgePlanTradeModel,
} from '../../../../Generated/Commodity-Management-Api';
import { HEDGE_KIND_ICONS, HedgePlanTriggerKind, readHedgePlanColor } from './HedgePlanTriggerStyles';

export const HEDGE_MARKER_SIZE = 26;

export function buildHedgeMarkerImage(icon: (typeof HEDGE_KIND_ICONS)[HedgePlanTriggerKind], lineColor: string): string {
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">' +
    `<circle cx="12" cy="12" r="11" fill="${readHedgePlanColor('--chart-marker-fill')}" stroke="${lineColor}" stroke-width="2"/>` +
    `<svg x="3.5" y="3.5" width="17" height="17" viewBox="0 0 24 24" fill="${lineColor}" color="${lineColor}">` +
    renderToStaticMarkup(createElement(icon)) +
    '</svg></svg>';

  return `url(data:image/svg+xml,${encodeURIComponent(svg)})`;
}

export interface AggregatedTrade {
  kind: HedgePlanTriggerKind;
  executionDate: string;
  executionPercent: number;
  /** Set when every trade in the group has a volume (the report's trades carry both). */
  executionVolume?: number;
  label: string;
  /** Volume-weighted average of the day's trade prices, when at least one trade in the group specified one. */
  price?: number;
}

// Trades on the same day, of the same kind, are combined into a single chart point: their
// volumes sum, and their price (when given) is volume-weighted rather than plotted separately.
export function aggregateTrades(trades: HedgePlanTradeModel[]): AggregatedTrade[] {
  const groupsByKindAndDay = new Map<string, HedgePlanTradeModel[]>();

  for (const trade of trades) {
    const day = trade.tradeDate.substring(0, 10);
    const key = `${trade.executionType}|${day}`;
    const group = groupsByKindAndDay.get(key);
    if (group) {
      group.push(trade);
    } else {
      groupsByKindAndDay.set(key, [trade]);
    }
  }

  return Array.from(groupsByKindAndDay.values()).map((group) => {
    const kind = group[0].executionType as HedgePlanTriggerKind;
    const day = group[0].tradeDate.substring(0, 10);
    const weight = (trade: HedgePlanTradeModel) => trade.volumePercent ?? trade.volume ?? 0;
    const executionPercent = group.reduce((sum, trade) => sum + (trade.volumePercent ?? 0), 0);
    const executionVolume = group.every((trade) => trade.volume !== null && trade.volume !== undefined)
      ? group.reduce((sum, trade) => sum + (trade.volume as number), 0)
      : undefined;

    const pricedTrades = group.filter((trade) => trade.price !== null && trade.price !== undefined);
    const pricedWeight = pricedTrades.reduce((sum, trade) => sum + weight(trade), 0);
    const price = pricedWeight > 0
      ? pricedTrades.reduce((sum, trade) => sum + weight(trade) * (trade.price as number), 0) / pricedWeight
      : undefined;

    return {
      kind,
      executionDate: day,
      executionPercent,
      executionVolume,
      label: `${kind} · ${group.length > 1 ? `${group.length} trades` : '1 trade'}`,
      price,
    };
  });
}

export function findMarketPriceOnOrBefore(matrix: HedgePlanTermReportModel, executionDate: string): number | undefined {
  const targetDate = executionDate.substring(0, 10);
  let latestDate: string | undefined;
  let latestPrice: number | undefined;

  for (const point of matrix.priceSeries) {
    if (point.price === null || point.price === undefined || !point.date) {
      continue;
    }

    const quoteDate = point.date.substring(0, 10);
    if (quoteDate <= targetDate && (latestDate === undefined || quoteDate > latestDate)) {
      latestDate = quoteDate;
      latestPrice = point.price;
    }
  }

  return latestPrice;
}
